/**
 * Retention Scale Component
 * 
 * Visual benchmarking tool for heat loss / thermal retention.
 * Shows a pointer moving from Red (poor retention) to Green (excellent retention).
 */

import { useEffect, useRef, useState } from 'react';
import '../portal/Portal.css';

interface RetentionScaleProps {
  /** Current heat loss in W/m² */
  currentValue: number;
  /** Target/recommended heat loss in W/m² */
  targetValue?: number;
  /** Label for the scale */
  label?: string;
}

export function RetentionScale({ 
  currentValue, 
  targetValue = 60, 
  label = "Building Retention Rating" 
}: RetentionScaleProps) {
  const [isIntersecting, setIsIntersecting] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Intersection Observer for viewport-based animation
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsIntersecting(true);
        }
      },
      { threshold: 0.3 }
    );

    observer.observe(container);

    return () => {
      observer.disconnect();
    };
  }, []);

  // Scale from 30W/m² (excellent) to 200W/m² (very poor)
  const minValue = 30; // Excellent passive house
  const maxValue = 200; // Very poor pre-1919 house
  
  // Helper to calculate position percentage on scale
  // Scale: 0% = left (Excellent / low heat loss) → 100% = right (Poor / high heat loss)
  const calculateRetentionPercent = (value: number, min: number, max: number): number => {
    return Math.max(0, Math.min(100, ((value - min) / (max - min)) * 100));
  };
  
  // Calculate positions
  const currentPercent = calculateRetentionPercent(currentValue, minValue, maxValue);
  const targetPercent = calculateRetentionPercent(targetValue, minValue, maxValue);

  // Color zones: Red (poor) → Amber → Yellow → Green (excellent)
  const getColor = (value: number): string => {
    if (value <= 60) return '#16a34a'; // Green: excellent
    if (value <= 90) return '#84cc16'; // Lime: good
    if (value <= 120) return '#eab308'; // Yellow: average
    if (value <= 150) return '#f59e0b'; // Amber: below average
    return '#dc2626'; // Red: poor
  };

  const getRating = (value: number): string => {
    if (value <= 60) return 'Excellent';
    if (value <= 90) return 'Good';
    if (value <= 120) return 'Average';
    if (value <= 150) return 'Below Average';
    return 'Poor';
  };

  return (
    <div 
      ref={containerRef}
      className="retention-scale"
      style={{
        padding: '1.5rem',
        background: 'var(--portal-surface)',
        borderRadius: 'var(--portal-radius)',
        border: '1px solid var(--portal-border)',
      }}
    >
      <div style={{ marginBottom: '1rem' }}>
        <h4 style={{ margin: '0 0 0.25rem 0', fontSize: '1rem', fontWeight: 600 }}>
          {label}
        </h4>
        <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--portal-text-secondary)' }}>
          {getRating(currentValue)} — {currentValue}W/m²
          {targetValue && targetValue !== currentValue && (
            <span> → Target: {targetValue}W/m² ({getRating(targetValue)})</span>
          )}
        </p>
      </div>

      {/* Scale bar with gradient */}
      <div style={{ position: 'relative', marginBottom: '0.5rem' }}>
        <div
          style={{
            height: '40px',
            background: 'linear-gradient(to right, #16a34a 0%, #84cc16 25%, #eab308 50%, #f59e0b 75%, #dc2626 100%)',
            borderRadius: '8px',
            position: 'relative',
            transform: 'translateZ(0)', // Hardware acceleration
            willChange: 'transform', // Performance hint
          }}
        >
          {/* Current value pointer */}
          <div
            className={isIntersecting ? 'retention-scale__pointer--animate' : ''}
            style={{
              position: 'absolute',
              left: `${currentPercent}%`,
              top: '50%',
              transform: 'translate(-50%, -50%)',
              width: '16px',
              height: '16px',
              background: '#ffffff',
              border: `3px solid ${getColor(currentValue)}`,
              borderRadius: '50%',
              boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
              zIndex: 2,
            }}
          >
            <div
              style={{
                position: 'absolute',
                bottom: '100%',
                left: '50%',
                transform: 'translateX(-50%)',
                marginBottom: '8px',
                padding: '4px 8px',
                background: getColor(currentValue),
                color: '#ffffff',
                borderRadius: '4px',
                fontSize: '0.75rem',
                fontWeight: 600,
                whiteSpace: 'nowrap',
              }}
            >
              Current
            </div>
          </div>

          {/* Target value pointer (if different) */}
          {targetValue && targetValue !== currentValue && (
            <div
              style={{
                position: 'absolute',
                left: `${targetPercent}%`,
                top: '50%',
                transform: 'translate(-50%, -50%)',
                width: '12px',
                height: '12px',
                background: '#ffffff',
                border: `2px dashed ${getColor(targetValue)}`,
                borderRadius: '50%',
                zIndex: 1,
              }}
            >
              <div
                style={{
                  position: 'absolute',
                  top: '100%',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  marginTop: '8px',
                  padding: '2px 6px',
                  background: getColor(targetValue),
                  color: '#ffffff',
                  borderRadius: '4px',
                  fontSize: '0.625rem',
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                }}
              >
                Goal
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Labels */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: '0.75rem',
          color: 'var(--portal-text-muted)',
        }}
      >
        <span>Excellent<br/>({minValue}W/m²)</span>
        <span style={{ textAlign: 'right' }}>Poor<br/>({maxValue}W/m²)</span>
      </div>

      {/* Analogy */}
      {currentValue > 120 && (
        <div
          style={{
            marginTop: '1rem',
            padding: '0.75rem',
            background: 'var(--portal-amber-bg)',
            borderRadius: 'var(--portal-radius-sm)',
            fontSize: '0.875rem',
            color: 'var(--portal-text)',
          }}
        >
          <strong>💡 What this means:</strong> Your home acts like a 'leaky bucket'—heat 
          escapes quickly through the walls and roof. Improving insulation would help retain 
          heat longer and reduce bills.
        </div>
      )}
    </div>
  );
}
